import { AxiosError } from 'axios'

import { CanvasErrorBody } from '@/api/canvas/types'
import { message } from '@/components'
import { ignoreErrorCodes } from '@/consts/errorMsg'
import { PrivilegeType } from '@/consts/team'
import { getToken } from '@/utils/auth'
import { formatErrCode, reportError, showError } from '@/utils/error'
import http, { createInstance, RequestConf } from '@/utils/http'

// TODO(model-route-api): the production base URL is announced at launch — driven
// entirely by VITE_CANVAS_API_BASE_URL until then; empty means same origin.
export const CANVAS_API_BASE_URL: string = import.meta.env.VITE_CANVAS_API_BASE_URL || ''

// Canvas auth follows the platform grant convention (same flow as Datadrive
// PlatformFSContext and the image upload client): POST /auth/grant with
// privilege_type 68 (canvas_access, no extra params), receive a JWT valid for
// 7 days, cache it locally and re-grant on expiry or auth failure.
const CANVAS_PRIVILEGE_TYPE: number = PrivilegeType.CanvasAccess

/** Server-side exp is 7 days; re-grant hourly to stay responsive to revocation. */
const CANVAS_TOKEN_CACHE_MS = 60 * 60 * 1000

/** models/http ErrCode: CheckJwtTokenFailed — arrives as HTTP 200 + {code, msg}. */
const CANVAS_CODE_JWT_FAILED = 176

export class CanvasApiError extends Error {
  status?: number
  code?: number
  /** True when the response interceptor already toasted this error. */
  notified = false

  constructor(message: string, status?: number, code?: number) {
    super(message)
    this.name = 'CanvasApiError'
    this.status = status
    this.code = code
  }
}

/**
 * Toast an error from a CanvasAPI call in a catch handler — no-op when the
 * response interceptor already toasted it (avoids the double-toast bug).
 */
export function showCanvasError(error: unknown): void {
  if (error instanceof CanvasApiError && error.notified) {
    return
  }
  message.error(error instanceof Error ? error.message : String(error))
}

let cachedGrant: { token: string; expiredAt: number } | null = null

/** Force a fresh grant on the next request (e.g. after an auth failure). */
export function invalidateCanvasToken(): void {
  cachedGrant = null
}

function toBearer(token: string): string {
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`
}

async function getCanvasToken(): Promise<string | null> {
  // Not logged in on the platform yet — skip the grant, let the request 401 naturally.
  if (!getToken()) {
    return null
  }

  if (cachedGrant && cachedGrant.expiredAt > Date.now()) {
    return cachedGrant.token
  }

  const { code, msg, token } = await http.post<{ token?: string }>('/auth/grant', {
    privilege_type: CANVAS_PRIVILEGE_TYPE,
  })

  if (code !== 0 || !token) {
    throw new CanvasApiError(msg || 'Failed to grant Canvas token', undefined, code)
  }

  cachedGrant = { token: toBearer(token), expiredAt: Date.now() + CANVAS_TOKEN_CACHE_MS }
  return cachedGrant.token
}

/**
 * Canvas error contract: business errors arrive as HTTP 200 + `{code, msg}`
 * (platform-compatible code set); non-200 responses guarantee NO body format —
 * judge those by HTTP status only.
 */
function normalizeCanvasError(error: AxiosError<CanvasErrorBody>): never {
  const body = error.response?.data
  const message = body?.msg || body?.error || error.message || 'Canvas API request failed'
  throw new CanvasApiError(message, error.response?.status, body?.code)
}

/** True when a 2xx body is actually a business-error envelope. */
function isBusinessError(body: unknown): body is CanvasErrorBody & { code: number } {
  return (
    typeof body === 'object' &&
    body !== null &&
    !Array.isArray(body) &&
    typeof (body as CanvasErrorBody).code === 'number' &&
    (body as CanvasErrorBody).code !== 0
  )
}

type CanvasRequestConf = RequestConf & { __canvasAuthRetried?: boolean }

/**
 * Mirrors the platform interceptor's toast behavior (utils/http.ts response handler)
 * for Canvas business errors: same errorMsg map, same per-request `errMsg` config
 * overrides, same unknown-code "+10000" offset, same platform ignore list (Canvas
 * shares the platform error-code ledger).
 */
function notifyCanvasBusinessError(
  body: CanvasErrorBody & { code: number },
  config: CanvasRequestConf,
): boolean {
  const { errMsg = {} } = config
  const errMap = formatErrCode(errMsg as Record<string, unknown>)

  let err = errMap[body.code] || errMap.default
  if (!errMap[body.code] && err === errMap.default) {
    err = { ...err, msg: `${err.msg}: ${body.code + 10000}` }
  }

  const sourceIgnoreCodes = ignoreErrorCodes.platform || []
  if (sourceIgnoreCodes.includes(body.code)) {
    return false
  }

  let notified = false
  if (errMsg !== false && !!err.msg) {
    showError(err.msg, String(body.code), err.type, 'platform')
    notified = true
  }
  reportError(body, err.msg, {
    url: config.url,
    method: config.method,
    data: config.data,
  })
  return notified
}

// Plain instance without the platform interceptors: Canvas has its own error
// shape and no team-token context.
const canvasHttp = createInstance(CANVAS_API_BASE_URL, 'canvas', false)

canvasHttp.interceptors.request.use(async (config) => {
  const token = await getCanvasToken()
  if (token && !config.headers?.['Authorization']) {
    config.headers = { ...config.headers, Authorization: token }
  }
  return config
})

canvasHttp.interceptors.response.use(
  (response) => {
    const body = response.data as unknown

    // Business errors come back as HTTP 200 + {code, msg}; success bodies are plain
    // resources without a `code` field, so a non-zero code is always an error.
    if (isBusinessError(body)) {
      const config = response.config as CanvasRequestConf

      // 176 = CheckJwtTokenFailed: drop the cached grant and retry once.
      if (body.code === CANVAS_CODE_JWT_FAILED) {
        if (!config.__canvasAuthRetried) {
          config.__canvasAuthRetried = true
          invalidateCanvasToken()
          if (config.headers) {
            delete config.headers['Authorization']
          }
          return canvasHttp.request(config)
        }
        // Retry exhausted: fail silently like the platform does for 176 (it redirects
        // instead of toasting); callers surface the error through their own states.
      } else {
        // The default Model Gateway project is auto-provisioned server-side on first
        // GET — 243 needs no client-side handling and surfaces like any other business
        // error if it ever happens.
        const notified = notifyCanvasBusinessError(body, config)
        const err = new CanvasApiError(
          body.msg || 'Canvas API request failed',
          response.status,
          body.code,
        )
        err.notified = notified
        throw err
      }

      throw new CanvasApiError(body.msg || 'Canvas API request failed', response.status, body.code)
    }

    return response.data
  },
  async (error: AxiosError<CanvasErrorBody>) => {
    const config = error.config as CanvasRequestConf | undefined

    // On the first 401 drop the cache and retry once with a freshly granted token
    // (same recovery as image-upload-client invalidate()).
    if (error.response?.status === 401 && config && !config.__canvasAuthRetried) {
      config.__canvasAuthRetried = true
      invalidateCanvasToken()
      if (config.headers) {
        delete config.headers['Authorization']
      }
      return canvasHttp.request(config)
    }

    return normalizeCanvasError(error)
  },
)

export default canvasHttp
